import { AnalyticsUserEvent } from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { capitalize } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import {
    BodyMediumRegular,
    IconSelectDropdown,
    PillButton,
    View,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { SIDEBAR_CONTENT_MAX_WIDTH } from '../../../common/utils/homeUtils';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import { track } from '../../../logger/Logger';
import useGetFreeToSpendData from '../../hooks/useGetFreeToSpendData';
import {
    FREE_TO_SPEND_TRANSACTIONS_QUERY_KEY,
    useGetFreeToSpendTransactions,
} from '../../hooks/useGetFreeToSpendTransactions';
import {
    FreeToSpendPeriodAtom,
    SelectedFreeToSpendAccountIndex,
    SelectedFreeToSpendAccountSyncteraId,
} from '../../state/FreeToSpendAtoms';
import type {
    FreeToSpendDataItem,
    FreeToSpendDataPoint,
    FreeToSpendPeriod,
} from '../../types/FreeToSpendTypes';
import { getPeriodRange } from './BudgetGraph';
import { SpendingStatusSheet } from './SpendingStatusSheet';
import { SpendingSummaryView } from './SpendingSummaryView';
import TransactionsSheet from './TransactionsSheet';

// Today for period filtering
const TODAY = new Date();

const getDateKey = (d: Date): string => {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const buildCumulativeSpendData = (
    spendData: FreeToSpendDataItem[],
    period: FreeToSpendPeriod,
    anchorDate: Date,
): FreeToSpendDataPoint[] => {
    const byDate = new Map<string, number>();
    for (const item of spendData) {
        byDate.set(item.date, (byDate.get(item.date) ?? 0) + item.spend);
    }

    const range = getPeriodRange(period, anchorDate);
    const start = range.start;
    const end = new Date(anchorDate);
    end.setHours(23, 59, 59, 999);

    const result: FreeToSpendDataPoint[] = [];
    let cumulative = 0;
    let cursor = new Date(start);
    while (cursor <= end) {
        const key = getDateKey(cursor);
        cumulative += byDate.get(key) ?? 0;
        result.push({
            date: new Date(cursor),
            amount: cumulative,
            transactions: spendData
                .filter((d) => d.date === key)
                .map((d) => d.transactions)
                .flat(),
        });
        cursor = new Date(cursor);
        cursor.setDate(cursor.getDate() + 1);
    }
    return result;
};

const SpendingSummary = () => {
    const user = useAuthedUser();

    const freeToSpendPeriod = useAtomValue(FreeToSpendPeriodAtom);
    const [
        selectedFreeToSpendAccountIndex,
        setSelectedFreeToSpendAccountIndex,
    ] = useAtom(SelectedFreeToSpendAccountIndex);

    // this fetches the bare minimum data required to show the budget graph and status
    const {
        data: freeToSpendData,
        isLoading: isLoadingFreeToSpendData,
        isError: isFreeToSpendError,
        refetch: refetchFreeToSpendData,
    } = useGetFreeToSpendData(user.memberJourneyLog?.completedMoneyMap);

    const accountBalances =
        freeToSpendData != null
            ? Object.entries(freeToSpendData).map(([id, acct]) => ({
                  id,
                  name: acct.name,
                  icon: acct.icon,
                  color: acct.color,
                  availableBalance: acct.availableBalance,
                  syncteraAccountId: acct.syncteraAccountId,
              }))
            : [];
    const selectedAccountId = useMemo(() => {
        return accountBalances[selectedFreeToSpendAccountIndex]?.id;
    }, [accountBalances, selectedFreeToSpendAccountIndex]);
    const selectedAccount = useMemo(() => {
        return selectedAccountId != null
            ? freeToSpendData?.[selectedAccountId]
            : undefined;
    }, [freeToSpendData, selectedAccountId]);
    const stats = useMemo(() => {
        return selectedAccount?.spendStats[freeToSpendPeriod];
    }, [selectedAccount, freeToSpendPeriod]);
    const [spendingStatusSheetOpen, setSpendingStatusSheetOpen] =
        useState(false);
    const setSelectedFreeToSpendAccountSyncteraId = useSetAtom(
        SelectedFreeToSpendAccountSyncteraId,
    );

    const { value: enableFreeToSpendTransactions } = useFeatureGate(
        FeatureFlags.ENABLE_FREE_TO_SPEND_TRANSACTIONS,
    );
    const queryClient = useQueryClient();
    const [isTransactionsSheetOpen, setIsTransactionsSheetOpen] =
        useState(false);

    // Summary data is enough for the graph. The transactions endpoint is the
    // large payload, so load it only while the sheet is open.
    const {
        data: freeToSpendTransactions,
        isLoading: isLoadingFreeToSpendTransactions,
        isError: isFreeToSpendTransactionsError,
        refetch: refetchFreeToSpendTransactions,
    } = useGetFreeToSpendTransactions(
        isTransactionsSheetOpen && freeToSpendData != null,
    );
    const sheetSpendData =
        selectedAccountId != null
            ? (freeToSpendTransactions?.[selectedAccountId] ?? [])
            : [];

    const closeTransactionsSheet = () => {
        setIsTransactionsSheetOpen(false);
        queryClient.removeQueries({
            queryKey: [FREE_TO_SPEND_TRANSACTIONS_QUERY_KEY],
        });
    };

    const budgetGraphSpendData = useMemo(
        () =>
            buildCumulativeSpendData(
                selectedAccount?.spendData ?? [],
                freeToSpendPeriod,
                TODAY,
            ),
        [selectedAccount?.spendData, freeToSpendPeriod],
    );

    useEffect(() => {
        if (
            setSelectedFreeToSpendAccountIndex != null &&
            accountBalances.length > 0 &&
            selectedFreeToSpendAccountIndex >= accountBalances.length
        ) {
            setSelectedFreeToSpendAccountIndex(0);
        }
    }, [
        accountBalances.length,
        selectedFreeToSpendAccountIndex,
        setSelectedFreeToSpendAccountIndex,
    ]);

    useEffect(() => {
        if (selectedAccount != null) {
            setSelectedFreeToSpendAccountSyncteraId(
                selectedAccount.syncteraAccountId,
            );
        }
    }, [selectedAccount]);

    if (!user.memberJourneyLog?.completedMoneyMap) {
        return null;
    }

    if (isFreeToSpendError && freeToSpendData == null) {
        return (
            <View
                width="100%"
                maxWidth={SIDEBAR_CONTENT_MAX_WIDTH}
                alignSelf="center"
            >
                <YStack
                    alignItems="center"
                    backgroundColor="$gray200"
                    borderRadius={24}
                    gap={16}
                    padding={24}
                >
                    <BodyMediumRegular color="$gray1000">
                        Could not load spending
                    </BodyMediumRegular>
                    <PillButton
                        text="Try again"
                        onPress={() => {
                            void refetchFreeToSpendData();
                        }}
                    />
                </YStack>
            </View>
        );
    }

    const { budget, currentSpend } = stats ?? {};
    const canOpenStatusSheet =
        !isLoadingFreeToSpendData &&
        (stats?.status === 'over' || stats?.status === 'slowDown');
    const title = `${capitalize(freeToSpendPeriod)} Spend`;
    const currentSpendLabel = `Current spend this ${freeToSpendPeriod === 'monthly' ? 'month' : 'week'}`;
    const leftInBudget = (budget ?? 0) - (currentSpend ?? 0);

    const breakdown = selectedAccount?.spendStats.breakdown[
        freeToSpendPeriod
    ] ?? {
        label: '',
        value: '',
    };
    const { label: breakdownLabel, value: breakdownValue } = breakdown;

    const accountSelector =
        accountBalances.length > 1 &&
        setSelectedFreeToSpendAccountIndex != null ? (
            <IconSelectDropdown
                options={accountBalances.map((account, index) => ({
                    value: String(index),
                    icon: account.icon,
                    label: account.name,
                    color: account.color,
                }))}
                value={String(selectedFreeToSpendAccountIndex)}
                onValueChange={(_value, index) => {
                    track(AnalyticsUserEvent.FTS_CHANGE_ACCOUNT);
                    setSelectedFreeToSpendAccountIndex(index);
                }}
                placeholder="Spend account"
            />
        ) : undefined;

    return (
        <>
            <View
                width="100%"
                maxWidth={SIDEBAR_CONTENT_MAX_WIDTH}
                alignSelf="center"
            >
                <SpendingSummaryView
                    title={title}
                    headerSlot={accountSelector}
                    status={stats?.status ?? null}
                    onStatusPress={
                        canOpenStatusSheet
                            ? () => {
                                  track(AnalyticsUserEvent.FTS_TAP_STATUS);
                                  setSpendingStatusSheetOpen(true);
                              }
                            : undefined
                    }
                    currentSpendLabel={currentSpendLabel}
                    currentSpendCents={currentSpend ?? null}
                    budgetCents={budget ?? null}
                    spendData={budgetGraphSpendData}
                    period={freeToSpendPeriod}
                    isOverBudget={stats?.status === 'over'}
                    leftInBudgetCents={leftInBudget}
                    timeLeft={stats?.timeLeft ?? null}
                    remainingLabel={
                        isLoadingFreeToSpendData
                            ? `Remaining per ${freeToSpendPeriod === 'weekly' ? 'day' : 'week'}`
                            : breakdownLabel
                    }
                    remainingValue={breakdownValue}
                    onGraphPress={() => {
                        if (enableFreeToSpendTransactions)
                            track(AnalyticsUserEvent.FTS_TAP_GRAPH);
                        setIsTransactionsSheetOpen(true);
                    }}
                    anchorDate={TODAY}
                    isLoading={isLoadingFreeToSpendData}
                />
            </View>
            <SpendingStatusSheet
                isOpen={spendingStatusSheetOpen}
                onClose={() => {
                    setSpendingStatusSheetOpen(false);
                }}
                period={freeToSpendPeriod}
            />
            <TransactionsSheet
                isOpen={isTransactionsSheetOpen}
                onClose={closeTransactionsSheet}
                spendPeriod={freeToSpendPeriod}
                allSpendData={sheetSpendData}
                budget={budget}
                isLoadingTransactions={isLoadingFreeToSpendTransactions}
                isTransactionsError={isFreeToSpendTransactionsError}
                onRetryTransactions={() => {
                    void refetchFreeToSpendTransactions();
                }}
            />
        </>
    );
};

export default SpendingSummary;
